import { Linking, Platform } from "react-native";
import { CalendarExportConfig, CalendarProvider } from "@/types";
import { format } from "date-fns";

/**
 * Generate ICS file content for calendar export
 */
const generateICS = (config: CalendarExportConfig): string => {
  const startDate = new Date(config.starts_at);
  const endDate = config.ends_at ? new Date(config.ends_at) : new Date(startDate.getTime() + 60 * 60 * 1000); // Default 1 hour

  const formatDateForICS = (date: Date, allDay: boolean): string => {
    if (allDay) {
      return format(date, "yyyyMMdd");
    }
    return format(date, "yyyyMMdd'T'HHmmss");
  };

  const escapeText = (text: string): string => {
    return text.replace(/[,;\\]/g, (match) => `\\${match}`).replace(/\n/g, "\\n");
  };

  const dtstart = config.all_day
    ? `DTSTART;VALUE=DATE:${formatDateForICS(startDate, true)}`
    : `DTSTART:${formatDateForICS(startDate, false)}`;

  const dtend = config.all_day
    ? `DTEND;VALUE=DATE:${formatDateForICS(endDate, true)}`
    : `DTEND:${formatDateForICS(endDate, false)}`;

  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Staatsen Familie App//NL",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${config.event_id}@staatsen-app`,
    `DTSTAMP:${format(new Date(), "yyyyMMdd'T'HHmmss'Z'")}`,
    dtstart,
    dtend,
    `SUMMARY:${escapeText(config.title)}`,
    config.description ? `DESCRIPTION:${escapeText(config.description)}` : "",
    config.location ? `LOCATION:${escapeText(config.location)}` : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");

  return icsContent;
};

/**
 * Generate Google Calendar URL
 */
const generateGoogleCalendarUrl = (config: CalendarExportConfig): string => {
  const startDate = new Date(config.starts_at);
  const endDate = config.ends_at ? new Date(config.ends_at) : new Date(startDate.getTime() + 60 * 60 * 1000);

  const formatDateForGoogle = (date: Date, allDay: boolean): string => {
    if (allDay) {
      return format(date, "yyyyMMdd");
    }
    return format(date, "yyyyMMdd'T'HHmmss");
  };

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: config.title,
    dates: `${formatDateForGoogle(startDate, config.all_day)}/${formatDateForGoogle(endDate, config.all_day)}`,
  });

  if (config.description) {
    params.append("details", config.description);
  }

  if (config.location) {
    params.append("location", config.location);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate Outlook/Microsoft Calendar URL
 */
const generateOutlookUrl = (config: CalendarExportConfig): string => {
  const startDate = new Date(config.starts_at);
  const endDate = config.ends_at ? new Date(config.ends_at) : new Date(startDate.getTime() + 60 * 60 * 1000);

  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: config.title,
    startdt: startDate.toISOString(),
    enddt: endDate.toISOString(),
    allday: config.all_day ? "true" : "false",
  });

  if (config.description) {
    params.append("body", config.description);
  }

  if (config.location) {
    params.append("location", config.location);
  }

  return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generate Apple Calendar URL (works best on iOS)
 */
const generateAppleCalendarUrl = (config: CalendarExportConfig): string => {
  // For Apple Calendar, we use a data URI with ICS content
  // This will prompt the user to add to their calendar
  const icsContent = generateICS(config);
  const encodedContent = encodeURIComponent(icsContent);
  return `data:text/calendar;charset=utf-8,${encodedContent}`;
};

/**
 * Export event to external calendar
 * @param config The calendar export configuration
 * @param provider The calendar provider to export to
 * @returns Promise that resolves when the calendar app is opened
 */
export const exportToCalendar = async (
  config: CalendarExportConfig,
  provider: CalendarProvider
): Promise<boolean> => {
  let url: string;

  switch (provider) {
    case "google":
      url = generateGoogleCalendarUrl(config);
      break;
    case "microsoft":
      url = generateOutlookUrl(config);
      break;
    case "apple":
      // For Apple, we try to use the native calendar app
      if (Platform.OS === "ios") {
        // On iOS, we can use the native calendar integration
        // For now, we'll use the ICS approach which prompts to add to calendar
        url = generateAppleCalendarUrl(config);
      } else {
        // On Android, fall back to ICS
        url = generateAppleCalendarUrl(config);
      }
      break;
    case "ics":
    default:
      url = generateAppleCalendarUrl(config);
      break;
  }

  try {
    const canOpen = await Linking.canOpenURL(url);
    if (canOpen) {
      await Linking.openURL(url);
      return true;
    } else {
      // For Google and Outlook, URLs should always be openable in browser
      if (provider === "google" || provider === "microsoft") {
        await Linking.openURL(url);
        return true;
      }
      console.warn("Cannot open calendar URL:", url);
      return false;
    }
  } catch (error) {
    console.error("Error opening calendar:", error);
    return false;
  }
};

/**
 * Get available calendar providers for the current platform
 */
export const getAvailableCalendarProviders = (): {
  provider: CalendarProvider;
  label: string;
  icon: string;
}[] => {
  const providers: { provider: CalendarProvider; label: string; icon: string }[] = [
    { provider: "google", label: "Google Agenda", icon: "logo-google" },
    { provider: "microsoft", label: "Outlook", icon: "mail" },
  ];

  if (Platform.OS === "ios") {
    providers.unshift({ provider: "apple", label: "Apple Agenda", icon: "logo-apple" });
  }

  providers.push({ provider: "ics", label: "Ander (ICS)", icon: "calendar" });

  return providers;
};

/**
 * Create CalendarExportConfig from AgendaEvent
 */
export const createCalendarExportConfig = (event: {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
}): Omit<CalendarExportConfig, "provider"> => ({
  event_id: event.id,
  title: event.title,
  description: event.description,
  location: event.location,
  starts_at: event.starts_at,
  ends_at: event.ends_at,
  all_day: event.all_day,
});

export default {
  exportToCalendar,
  getAvailableCalendarProviders,
  createCalendarExportConfig,
  generateICS,
};

